// Student profile tabs for records: Payments (history incl. refunds),
// Documents (uploads), Remarks (discipline/behaviour, optionally pushed to
// the parent) and Leave.
import { useEffect, useState } from 'react';
import { Upload, Eye, Trash2, ThumbsUp, ThumbsDown, StickyNote, Bell } from 'lucide-react';
import {
  getPaymentHistory, getStudentDocuments, getStudentDocument, uploadStudentDocument, deleteStudentDocument,
  getStudentRemarks, addStudentRemark, deleteStudentRemark,
  getStudentLeaves, addStudentLeave, decideStudentLeave, deleteStudentLeave,
} from '../../api/client';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import { Card, CardHeader } from '../../ui/Card';
import { Field, Msg, errText, inputCls, theadRowCls, EmptyRow } from '../../ui/Form';
import { inr, fmtDate, todayISO, fileToUpload, openDataUrl } from '../../ui/format';

// ── Payments ────────────────────────────────────────────────
export function PaymentsTab({ studentId }) {
  const [data, setData] = useState(null);
  const [msg,  setMsg]  = useState('');
  useEffect(() => { getPaymentHistory(studentId).then(setData).catch(err => setMsg(errText(err))); }, [studentId]);
  if (msg) return <Msg msg={msg} />;
  if (!data) return <p className="text-sm text-ink-soft">Loading…</p>;
  return (
    <Card>
      <CardHeader title="Payment history" action={
        <span className="text-sm text-ink-soft">Paid <b className="text-success">{inr(data.paid_total)}</b>
          {data.refund_total > 0 && <> · Refunded <b className="text-critical">{inr(data.refund_total)}</b> · Net <b className="text-ink">{inr(data.net)}</b></>}</span>
      } />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Details</th><th className="px-4 py-2.5">Mode</th><th className="px-4 py-2.5">Receipt</th><th className="px-4 py-2.5 text-right">Amount</th></tr></thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-t border-line-soft">
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-4 py-3">{r.type === 'refund' ? <><Badge tone="critical">Refund</Badge> <span className="ml-1">{r.description}</span></> : r.description}</td>
                <td className="px-4 py-3">{r.mode || '—'}{r.ref ? <span className="block text-xs text-ink-soft">Ref. {r.ref}</span> : null}</td>
                <td className="px-4 py-3 text-ink-soft tabular-nums">{r.receipt_no ? `#${r.receipt_no}` : '—'}</td>
                <td className={`px-4 py-3 text-right font-bold tabular-nums ${r.type === 'refund' ? 'text-critical' : 'text-ink'}`}>{r.type === 'refund' ? '−' : ''}{inr(r.amount)}</td>
              </tr>
            ))}
            {!data.rows.length && <EmptyRow cols={5}>No payments yet</EmptyRow>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Documents ───────────────────────────────────────────────
const DOC_TYPES = ['Birth certificate', 'Transfer certificate', 'Aadhaar card', 'Previous marksheet', 'Photo', 'Medical certificate', 'Caste certificate', 'Other'];

export function DocumentsTab({ studentId }) {
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState(DOC_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState('');
  useEffect(() => { load(); }, [studentId]);
  async function load() { try { setDocs(await getStudentDocuments(studentId)); } catch (err) { setMsg(errText(err)); } }

  async function upload(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setMsg('');
    try {
      const data_url = await fileToUpload(file);
      await uploadStudentDocument(studentId, { doc_type: type, file_name: file.name, data_url });
      setMsg(`${type} uploaded ✅`); load();
    } catch (err) { setMsg(errText(err)); }
    finally { setBusy(false); }
  }
  async function view(d) { try { openDataUrl((await getStudentDocument(d.id)).data_url); } catch (err) { setMsg(errText(err)); } }
  async function remove(d) {
    if (!window.confirm(`Delete ${d.doc_type} (${d.file_name})?`)) return;
    try { await deleteStudentDocument(d.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  return (
    <div className="flex flex-col gap-3">
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <Field label="Document type">
          <select className={inputCls} value={type} onChange={e => setType(e.target.value)}>{DOC_TYPES.map(t => <option key={t}>{t}</option>)}</select>
        </Field>
        <label className={`flex items-center gap-1.5 font-bold text-sm px-4 py-2.5 rounded-s cursor-pointer bg-transit text-white hover:bg-transit-deep ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload size={14} /> {busy ? 'Uploading…' : 'Upload PDF or photo'}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} />
        </label>
        <span className="text-xs text-ink-soft">Max 2 MB per file. Photos are shrunk automatically.</span>
      </Card>
      <Msg msg={msg} />
      <Card>
        <table className="w-full text-sm">
          <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Document</th><th className="px-4 py-2.5">File</th><th className="px-4 py-2.5">Uploaded</th><th className="px-4 py-2.5"></th></tr></thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} className="border-t border-line-soft">
                <td className="px-4 py-3 font-semibold">{d.doc_type}</td>
                <td className="px-4 py-3 text-ink-soft">{d.file_name} <span className="text-xs">· {d.size_kb} KB</span></td>
                <td className="px-4 py-3">{fmtDate(d.uploaded_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => view(d)} className="inline-flex items-center gap-1 text-xs font-bold text-transit-deep hover:underline mr-3"><Eye size={13} /> View</button>
                  <button onClick={() => remove(d)} aria-label="Delete document" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {!docs.length && <EmptyRow cols={4}>No documents uploaded</EmptyRow>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── Remarks ─────────────────────────────────────────────────
const KINDS = {
  positive: { label: 'Appreciation', icon: ThumbsUp, tone: 'success' },
  negative: { label: 'Discipline', icon: ThumbsDown, tone: 'critical' },
  note:     { label: 'Note', icon: StickyNote, tone: 'neutral' },
};

export function RemarksTab({ studentId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ kind: 'positive', remark: '', remark_date: todayISO(), notify: true });
  const [msg,  setMsg]  = useState('');
  useEffect(() => { load(); }, [studentId]);
  async function load() { try { setRows(await getStudentRemarks(studentId)); } catch (err) { setMsg(errText(err)); } }

  async function add(e) {
    e.preventDefault(); setMsg('');
    try {
      const r = await addStudentRemark(studentId, form);
      setMsg(form.notify ? (r.notified ? 'Remark saved and sent to the parent ✅' : 'Remark saved ✅ (parent doesn\'t have the app, so no push was sent)') : 'Remark saved ✅');
      setForm({ ...form, remark: '' }); load();
    } catch (err) { setMsg(errText(err)); }
  }
  async function remove(r) {
    if (!window.confirm('Delete this remark?')) return;
    try { await deleteStudentRemark(r.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  return (
    <div className="grid grid-cols-[minmax(260px,360px)_1fr] gap-4 items-start max-[900px]:grid-cols-1">
      <Card className="p-4">
        <form onSubmit={add} className="flex flex-col gap-3">
          <div className="flex gap-1.5">
            {Object.entries(KINDS).map(([k, v]) => (
              <button type="button" key={k} onClick={() => setForm({ ...form, kind: k })}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold border ${form.kind === k ? 'bg-transit text-white border-transit' : 'border-line text-ink'}`}>
                <v.icon size={12} /> {v.label}
              </button>
            ))}
          </div>
          <Field label="Remark"><textarea required rows={3} className={inputCls} value={form.remark} onChange={e => setForm({ ...form, remark: e.target.value })} /></Field>
          <Field label="Date"><input type="date" className={inputCls} value={form.remark_date} onChange={e => setForm({ ...form, remark_date: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm font-semibold text-ink">
            <input type="checkbox" checked={form.notify} onChange={e => setForm({ ...form, notify: e.target.checked })} className="accent-[var(--transit)]" />
            Send to the parent's phone
          </label>
          <Button type="submit" className="w-fit">Save remark</Button>
          <Msg msg={msg} />
        </form>
      </Card>
      <Card>
        {rows.map(r => {
          const k = KINDS[r.kind];
          return (
            <div key={r.id} className="flex items-start gap-3 px-5 py-3 border-t border-line-soft first:border-t-0">
              <Badge tone={k.tone}>{k.label}</Badge>
              <div className="flex-1">
                <p className="text-sm text-ink">{r.remark}</p>
                <p className="text-xs text-ink-soft mt-0.5">{fmtDate(r.remark_date)}{r.notified && <span className="inline-flex items-center gap-1 ml-2"><Bell size={11} /> sent to parent</span>}</p>
              </div>
              <button onClick={() => remove(r)} aria-label="Delete remark" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button>
            </div>
          );
        })}
        {!rows.length && <p className="px-5 py-6 text-sm text-ink-soft">No remarks yet.</p>}
      </Card>
    </div>
  );
}

// ── Leave ───────────────────────────────────────────────────
const LEAVE_TONE = { approved: 'success', pending: 'caution', rejected: 'critical' };

export function LeaveTab({ studentId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ from_date: todayISO(), to_date: todayISO(), reason: '', notify: true });
  const [msg,  setMsg]  = useState('');
  useEffect(() => { load(); }, [studentId]);
  async function load() { try { setRows(await getStudentLeaves(studentId)); } catch (err) { setMsg(errText(err)); } }

  async function add(e) {
    e.preventDefault(); setMsg('');
    try { await addStudentLeave(studentId, { ...form, status: 'approved' }); setMsg('Leave recorded ✅'); setForm({ ...form, reason: '' }); load(); }
    catch (err) { setMsg(errText(err)); }
  }
  async function decide(l, status) {
    try { await decideStudentLeave(l.id, { status, notify: true }); load(); } catch (err) { setMsg(errText(err)); }
  }
  async function remove(l) {
    if (!window.confirm('Delete this leave?')) return;
    try { await deleteStudentLeave(l.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  const totalDays = rows.filter(l => l.status === 'approved').reduce((s, l) => s + l.days, 0);
  return (
    <div className="grid grid-cols-[minmax(260px,340px)_1fr] gap-4 items-start max-[900px]:grid-cols-1">
      <Card className="p-4">
        <form onSubmit={add} className="flex flex-col gap-3">
          <h3 className="font-display font-bold text-[15px] text-ink">Record leave</h3>
          <div className="grid grid-cols-2 gap-2">
            <Field label="From"><input required type="date" className={inputCls} value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value, to_date: e.target.value > form.to_date ? e.target.value : form.to_date })} /></Field>
            <Field label="To"><input required type="date" min={form.from_date} className={inputCls} value={form.to_date} onChange={e => setForm({ ...form, to_date: e.target.value })} /></Field>
          </div>
          <Field label="Reason"><input required className={inputCls} placeholder="e.g. Fever, family function" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm font-semibold text-ink">
            <input type="checkbox" checked={form.notify} onChange={e => setForm({ ...form, notify: e.target.checked })} className="accent-[var(--transit)]" />
            Confirm to the parent by push
          </label>
          <Button type="submit" className="w-fit">Save</Button>
          <Msg msg={msg} />
        </form>
      </Card>
      <Card>
        <CardHeader title={`Leave (${totalDays} day${totalDays === 1 ? '' : 's'} approved)`} />
        {rows.map(l => (
          <div key={l.id} className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-line-soft first:border-t-0">
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm font-semibold text-ink">{fmtDate(l.from_date)}{l.to_date !== l.from_date ? ` – ${fmtDate(l.to_date)}` : ''} <span className="text-ink-soft font-normal">· {l.days} day{l.days === 1 ? '' : 's'}</span></p>
              <p className="text-xs text-ink-soft">{l.reason}</p>
            </div>
            <Badge tone={LEAVE_TONE[l.status]}>{l.status}</Badge>
            {l.status === 'pending' && (
              <>
                <Button className="!px-2.5 !py-1 !text-xs" onClick={() => decide(l, 'approved')}>Approve</Button>
                <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => decide(l, 'rejected')}>Reject</Button>
              </>
            )}
            <button onClick={() => remove(l)} aria-label="Delete leave" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button>
          </div>
        ))}
        {!rows.length && <p className="px-5 py-6 text-sm text-ink-soft">No leave recorded.</p>}
      </Card>
    </div>
  );
}
